import get from 'lodash.get';
import { gatingRulesService as gatingRulesServiceForDibels8 } from '@amplify/dibels8-composite-score';
import { gatingRulesService as gatingRulesServiceForIdel2 } from '@amplify/lectura-composite-score';
import { DIBELS8, IDEL2 } from '../../constants/applications';
import { DISCONTINUE_KEY, TESTED_OUT_KEY } from '../../constants/gatingRules';
import Rule from './Rule';

/**
 * Get gating rules for window for Dibels8
 * @param {Array} studentResults student's measure results for window
 * @param {string} grade
 * @param {string} period
 * @param {Object} gatedMeasuresStatus status of gated measures
 *  {
 *      <measure>: {
 *          gatingType: ...,
 *          imputedScore: ...,
 *          interpretation: ...
 *          }
 *  }
 * @returns {{gatingMeasure:?string, gatedMeasures:Array, ruleType:?string, isRuleMet:?boolean}} object with
 * current gating rules
 */
export const getGatingRulesForDibesl8 = (studentResults, grade, period, gatedMeasuresStatus) => {
    const gatingRuleForWindow = gatingRulesServiceForDibels8.getGatingRuleForWindow(grade, period);

    const gatingRule = new Rule(null, [], null, null);

    if (gatingRuleForWindow) {
        gatingRule.constructRule(gatingRuleForWindow, studentResults, gatedMeasuresStatus);
    }

    return gatingRule.getRuleAtributes();
};

/**
 * Get gating rules for window for Lectura
 * @param {Array} studentResults student's measure results for window
 * @param {string} grade
 * @param {string} period
 * @param {Object} gatedMeasuresStatus status of gated measures
 *  {
 *      <measure>: {
 *          gatingType: ...,
 *          imputedScore: ...,
 *          interpretation: ...
 *          }
 *  }
 * @returns {[{gatingMeasure:?string, gatedMeasures:Array, ruleType:?string, isRuleMet:?boolean}
 *  {gatingMeasure:?string, gatedMeasures:Array, ruleType:?string, isRuleMet:?boolean}]}  array of objects which contain
 * information about gating rules [ discontinueRule, testedOutRule ]
 */
export const getGatingRulesForIdel2 = (studentResults, grade, period, gatedMeasuresStatus) => {
    const gatingRulesForWindow = gatingRulesServiceForIdel2.getGatingRulesForWindow(grade, period);
    const discontinueRule = new Rule(null, [], null, null);
    const testedOutRule = new Rule(null, [], null, null);

    if (gatingRulesForWindow) {
        const discontinueRuleDescription = get(gatingRulesForWindow, DISCONTINUE_KEY, null);
        const testedOutRuleDescription = get(gatingRulesForWindow, TESTED_OUT_KEY, null);

        if (discontinueRuleDescription) {
            discontinueRule.constructRule(
                discontinueRuleDescription,
                studentResults,
                gatedMeasuresStatus[DISCONTINUE_KEY],
            );
        }

        if (testedOutRuleDescription) {
            testedOutRule.constructRule(
                testedOutRuleDescription,
                studentResults,
                gatedMeasuresStatus[TESTED_OUT_KEY],
            );
        }
    }

    return [discontinueRule.getRuleAtributes(), testedOutRule.getRuleAtributes()];
};

const gatingRules = {
    [DIBELS8]: getGatingRulesForDibesl8,
    [IDEL2]: getGatingRulesForIdel2,
};

/**
 * Get gating rules for window based on application
 * @param {Array} studentResults student's measure results for window
 * @param {string} grade
 * @param {string} period
 * @param {string} appName
 * @param {Object} gatedMeasuresStatus status of gated measures
 *  {
 *      <measure>: {
 *          gatingType: ...,
 *          imputedScore: ...,
 *          interpretation: ...
 *          }
 *  }
 * @returns object which contain info about gatingRules
 */
export const getGatingRules = (studentResults, grade, period, gatedMeasuresStatus, appName) =>
    gatingRules[appName](studentResults, grade, period, gatedMeasuresStatus);
